import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Landmark, Phone, Copy, Check, ShoppingCart, ArrowRight, PartyPopper, ExternalLink, MessageCircle } from "lucide-react";
import { API, useShop } from "@/shop/ShopContext";
import { Reveal } from "@/components/Reveal";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#1C2028] px-4 py-3 text-sm text-neutral-100 placeholder:text-neutral-500 focus:border-[#FF5500] focus:outline-none";

const Checkout = () => {
  const { config, cart, total, clearCart } = useShop();
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", city: "", cap: "", note: "" });
  const [payment, setPayment] = useState("card");
  const [sending, setSending] = useState(false);
  const [order, setOrder] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.city || !form.cap) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/orders`, {
        customer: form,
        items: cart.map((i) => ({ product_id: i.id, qty: i.qty })),
        payment_method: payment,
      });
      setOrder(res.data);
      clearCart();
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error("Errore durante l'invio dell'ordine. Riprova o chiamaci.", {
        description: config.phone,
      });
    } finally {
      setSending(false);
    }
  };

  const copyIban = () => {
    navigator.clipboard.writeText(config.iban?.iban || "");
    setCopied(true);
    toast.success("IBAN copiato");
    setTimeout(() => setCopied(false), 2000);
  };

  if (order) {
    const waText = encodeURIComponent(
      [
        `NUOVO ORDINE ${order.ref} — Delivery Ricambi`,
        "",
        ...order.items.map((i) => `${i.qty} x ${i.name} (${i.code}) — € ${i.line_total.toFixed(2)}`),
        "",
        `Totale: € ${order.total.toFixed(2)}`,
        `Pagamento: ${order.payment_method === "card" ? "Carta (POS SumUp)" : "Bonifico bancario"}`,
        "",
        `Cliente: ${order.customer.name}`,
        `Telefono: ${order.customer.phone}`,
        order.customer.email ? `Email: ${order.customer.email}` : "",
        `Consegna: ${order.customer.address}, ${order.customer.city} ${order.customer.cap}`,
        order.customer.note ? `Note: ${order.customer.note}` : "",
      ]
        .filter(Boolean)
        .join("\n")
    );
    const waLink = `${config.whatsapp}?text=${waText}`;
    return (
      <div className="mx-auto max-w-3xl px-4 py-16" data-testid="order-confirmation-modal">
        <Reveal>
          <div className="rounded-2xl border border-[#FF5500]/40 bg-[#14171D] p-8 sm:p-10">
            <div className="flex items-center gap-3">
              <PartyPopper className="h-8 w-8 text-[#FF5500]" />
              <h1 className="font-display text-3xl font-bold uppercase tracking-tight text-neutral-100 sm:text-4xl">
                Ordine ricevuto!
              </h1>
            </div>
            <p className="mt-3 text-sm text-neutral-400">
              Grazie {order.customer.name}. Il tuo ordine è stato registrato: ti chiameremo per conferma.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-white/10 bg-[#0C0E12] p-5">
              <div>
                <span className="block text-[11px] uppercase tracking-widest text-neutral-500">Numero ordine</span>
                <span className="font-mono text-2xl font-semibold text-[#FF5500]" data-testid="order-ref">{order.ref}</span>
              </div>
              <div className="text-right">
                <span className="block text-[11px] uppercase tracking-widest text-neutral-500">Totale</span>
                <span className="font-display text-3xl text-neutral-100" data-testid="order-total">€ {order.total.toFixed(2)}</span>
              </div>
            </div>

            <div className="mt-6 space-y-2">
              {order.items.map((i) => (
                <div key={i.product_id} className="flex justify-between border-b border-white/5 pb-2 text-sm">
                  <span className="text-neutral-300">
                    {i.qty} × {i.name} <span className="ml-1 font-mono text-[11px] text-neutral-500">{i.code}</span>
                  </span>
                  <span className="font-mono text-neutral-200">€ {i.line_total.toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-6" data-testid="whatsapp-box">
              <h2 className="flex items-center gap-2 font-display text-xl font-semibold uppercase tracking-wide text-neutral-100">
                <MessageCircle className="h-5 w-5 text-emerald-400" /> Ultimo passo: avvisa il negozio su WhatsApp
              </h2>
              <p className="mt-2 text-sm text-neutral-300">
                Clicca il pulsante: si apre WhatsApp con tutti i dettagli dell'ordine già scritti (ricambi,
                indirizzo di consegna e pagamento). Il messaggio arriva subito al negozio sul numero{" "}
                <span className="font-semibold text-neutral-100">{config.phone}</span>.
              </p>
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-order-btn"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 font-display text-sm uppercase tracking-[0.2em] text-[#0C0E12] transition-transform hover:scale-105"
              >
                <MessageCircle className="h-4 w-4" /> Invia ordine su WhatsApp
              </a>
            </div>

            {order.payment_method === "card" ? (
              <div className="mt-8 rounded-xl border border-[#FF5500]/30 bg-[#FF5500]/5 p-6" data-testid="card-payment-box">
                <h2 className="flex items-center gap-2 font-display text-xl font-semibold uppercase tracking-wide text-neutral-100">
                  <CreditCard className="h-5 w-5 text-[#FF5500]" /> Completa il pagamento con carta
                </h2>
                {config.pos_link ? (
                  <>
                    <p className="mt-2 text-sm text-neutral-300">
                      Clicca il pulsante: si apre la pagina di pagamento sicura del nostro POS.
                    </p>
                    <a
                      href={config.pos_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="pos-pay-btn"
                      className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#FF5500] px-7 py-3.5 font-display text-sm uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#E64D00]"
                    >
                      Paga € {order.total.toFixed(2)} con carta <ExternalLink className="h-4 w-4" />
                    </a>
                  </>
                ) : (
                  <p className="mt-2 text-sm text-neutral-300">
                    Il link di pagamento POS è in fase di attivazione: ti chiamiamo noi al{" "}
                    <span className="font-semibold text-neutral-100">{order.customer.phone}</span> per completare
                    il pagamento con carta al telefono, oppure chiamaci tu al numero qui sotto.
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-8 rounded-xl border border-white/10 bg-[#0C0E12] p-6" data-testid="bonifico-box">
                <h2 className="flex items-center gap-2 font-display text-xl font-semibold uppercase tracking-wide text-neutral-100">
                  <Landmark className="h-5 w-5 text-[#FF5500]" /> Coordinate per il bonifico
                </h2>
                <p className="mt-2 text-sm text-neutral-400">
                  Spediamo appena riceviamo l'accredito. Usa il numero d'ordine come causale.
                </p>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-neutral-500">Intestatario</dt>
                    <dd className="font-semibold text-neutral-100">{config.iban?.intestatario}</dd>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <dt className="text-neutral-500">IBAN</dt>
                    <dd className="flex items-center gap-2">
                      <span className="font-mono text-neutral-100" data-testid="iban-value">{config.iban?.iban}</span>
                      <button onClick={copyIban} data-testid="copy-iban-order-btn" className="rounded-md border border-white/15 p-1.5 hover:border-[#FF5500]" aria-label="Copia IBAN">
                        {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-neutral-500">Banca</dt>
                    <dd className="text-neutral-100">{config.iban?.banca}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-neutral-500">BIC / SWIFT</dt>
                    <dd className="font-mono text-neutral-100">{config.iban?.bic}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-neutral-500">Causale</dt>
                    <dd className="font-mono text-[#FF5500]">Ordine {order.ref}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-neutral-500">Importo</dt>
                    <dd className="font-display text-xl text-neutral-100">€ {order.total.toFixed(2)}</dd>
                  </div>
                </dl>
              </div>
            )}

            <div className="mt-8 flex flex-col items-center gap-4 border-t border-white/10 pt-6 sm:flex-row sm:justify-between">
              <a href={config.phone_href} className="flex items-center gap-2 font-display text-lg uppercase tracking-wider text-[#FF5500]" data-testid="confirmation-phone">
                <Phone className="h-4 w-4" /> {config.phone}
              </a>
              <Link
                to="/catalogo"
                data-testid="back-to-catalog-btn"
                className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-display text-sm uppercase tracking-[0.2em] text-neutral-100 transition-colors hover:border-[#FF5500] hover:text-[#FF5500]"
              >
                Torna al catalogo <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="mx-auto flex max-w-xl flex-col items-center gap-5 px-4 py-28 text-center" data-testid="checkout-empty">
        <ShoppingCart className="h-14 w-14 text-neutral-600" />
        <h1 className="font-display text-3xl font-bold uppercase text-neutral-100">Il carrello è vuoto</h1>
        <p className="text-sm text-neutral-400">Aggiungi qualche ricambio prima di procedere al checkout.</p>
        <Link
          to="/catalogo"
          className="rounded-full bg-[#FF5500] px-7 py-3.5 font-display text-sm uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#E64D00]"
          data-testid="empty-checkout-catalog-btn"
        >
          Vai al catalogo
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-14" data-testid="checkout-page">
      <Reveal>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight text-neutral-100 sm:text-5xl">Checkout</h1>
        <p className="mt-3 flex items-center gap-2 text-sm text-neutral-400">
          <Phone className="h-4 w-4 text-[#FF5500]" />
          Problemi con l'ordine? Chiamaci:{" "}
          <a href={config.phone_href} className="font-semibold text-[#FF5500]" data-testid="checkout-phone-link">
            {config.phone}
          </a>
        </p>
      </Reveal>

      <form onSubmit={submit} className="mt-10 grid gap-8 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-3">
          <div className="rounded-2xl border border-white/10 bg-[#14171D] p-6 sm:p-8" data-testid="shipping-form">
            <h2 className="font-display text-xl font-semibold uppercase tracking-wide text-neutral-100">
              Dati di spedizione
            </h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <input data-testid="input-name" className={inputCls} placeholder="Nome e cognome *" value={form.name} onChange={set("name")} />
              <input data-testid="input-phone" className={inputCls} placeholder="Telefono *" value={form.phone} onChange={set("phone")} />
              <input data-testid="input-email" className={`${inputCls} sm:col-span-2`} placeholder="Email (facoltativa)" type="email" value={form.email} onChange={set("email")} />
              <input data-testid="input-address" className={`${inputCls} sm:col-span-2`} placeholder="Indirizzo e numero civico *" value={form.address} onChange={set("address")} />
              <input data-testid="input-city" className={inputCls} placeholder="Città *" value={form.city} onChange={set("city")} />
              <input data-testid="input-cap" className={inputCls} placeholder="CAP *" value={form.cap} onChange={set("cap")} />
              <textarea data-testid="input-note" className={`${inputCls} sm:col-span-2`} rows={3} placeholder="Note per il negozio (facoltative)" value={form.note} onChange={set("note")} />
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#14171D] p-6 sm:p-8">
            <h2 className="font-display text-xl font-semibold uppercase tracking-wide text-neutral-100">
              Metodo di pagamento
            </h2>
            <div className={`mt-5 grid gap-4 ${config.bonifico_enabled ? "sm:grid-cols-2" : ""}`}>
              <button
                type="button"
                onClick={() => setPayment("card")}
                data-testid="payment-method-card"
                className={`rounded-xl border p-5 text-left transition-colors ${
                  payment === "card" ? "border-[#FF5500] bg-[#FF5500]/10" : "border-white/10 bg-[#1C2028] hover:border-white/25"
                }`}
              >
                <CreditCard className="h-6 w-6 text-[#FF5500]" />
                <span className="mt-3 block font-display text-base font-semibold uppercase tracking-wide text-neutral-100">
                  Carta di credito
                </span>
                <span className="mt-1 block text-xs text-neutral-400">
                  Pagamento online tramite POS SumUp del negozio. Istantaneo e sicuro.
                </span>
              </button>
              {config.bonifico_enabled && (
                <button
                  type="button"
                  onClick={() => setPayment("bonifico")}
                  data-testid="payment-method-bonifico"
                  className={`rounded-xl border p-5 text-left transition-colors ${
                    payment === "bonifico" ? "border-[#FF5500] bg-[#FF5500]/10" : "border-white/10 bg-[#1C2028] hover:border-white/25"
                  }`}
                >
                  <Landmark className="h-6 w-6 text-[#FF5500]" />
                  <span className="mt-3 block font-display text-base font-semibold uppercase tracking-wide text-neutral-100">
                    Bonifico bancario
                  </span>
                  <span className="mt-1 block text-xs text-neutral-400">
                    Coordinate IBAN alla conferma. Spedizione all'accredito.
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="sticky top-32 rounded-2xl border border-white/10 bg-[#14171D] p-6 sm:p-8" data-testid="order-summary">
            <h2 className="font-display text-xl font-semibold uppercase tracking-wide text-neutral-100">
              Riepilogo ordine
            </h2>
            <div className="mt-5 max-h-72 space-y-3 overflow-y-auto pr-1">
              {cart.map((i) => (
                <div key={i.id} className="flex items-center gap-3">
                  <img src={i.image} alt={i.name} className="h-12 w-12 rounded-md object-cover" />
                  <div className="flex-1">
                    <span className="block text-sm font-medium leading-tight text-neutral-200">{i.name}</span>
                    <span className="font-mono text-[11px] text-neutral-500">
                      {i.qty} × € {i.price.toFixed(2)}
                    </span>
                  </div>
                  <span className="font-mono text-sm text-neutral-200">€ {(i.qty * i.price).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-2 border-t border-white/10 pt-4 text-sm">
              <div className="flex justify-between text-neutral-400">
                <span>Spedizione</span>
                <span>Calcolata alla conferma telefonica</span>
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="uppercase tracking-widest text-neutral-400">Totale</span>
                <span className="font-display text-3xl text-[#FF5500]" data-testid="checkout-total">€ {total.toFixed(2)}</span>
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="confirm-order-btn"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-[#FF5500] py-4 font-display text-base uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#E64D00] disabled:opacity-60"
            >
              {sending ? "Invio in corso..." : "Conferma ordine"}
              <ArrowRight className="h-4 w-4" />
            </button>
            <p className="mt-4 text-center text-xs text-neutral-500">
              Assistenza: {config.phone} · Lun–Sab 9:00–19:00
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
